import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNum } from "@/lib/api";
import { PageHeader, Panel, LoadingState, EmptyState, ErrorState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { Eye, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 12;

export default function MarketWatch() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [assetClass, setAssetClass] = useState("all");
  const [exchange, setExchange] = useState("all");
  const [sortBy, setSortBy] = useState("symbol");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["instruments", search, assetClass, exchange, sortBy, order, page],
    queryFn: async () =>
      (await api.get("/instruments", {
        params: {
          search,
          asset_class: assetClass === "all" ? "" : assetClass,
          exchange: exchange === "all" ? "" : exchange,
          sort_by: sortBy, order, page, page_size: PAGE_SIZE,
        },
      })).data,
    placeholderData: keepPreviousData,
  });

  const toggleSort = (col) => {
    if (sortBy === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setSortBy(col); setOrder("asc"); }
    setPage(1);
  };

  const SortHead = ({ col, label, align = "left" }) => (
    <th
      data-testid={`sort-${col}`}
      onClick={() => toggleSort(col)}
      className={cn("px-3 py-2 font-medium text-slate-400 cursor-pointer select-none hover:text-slate-200", align === "right" && "text-right")}
    >
      <span className={cn("inline-flex items-center gap-1", align === "right" && "flex-row-reverse")}>
        {label}
        {sortBy === col ? (order === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );

  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title="Market Watch"
        subtitle="Universal multi-asset instrument directory · DEMO pricing"
        icon={Eye}
        testId="market-watch-header"
      />

      <Panel testId="market-watch-panel">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[var(--border-subtle)]">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="instrument-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search symbol or name..."
              className="h-9 pl-8 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200"
            />
          </div>
          <Select value={assetClass} onValueChange={(v) => { setAssetClass(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-asset-class" className="h-9 w-[160px] text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200">
              <SelectValue placeholder="Asset Class" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
              <SelectItem value="all">All Asset Classes</SelectItem>
              {(data?.asset_classes || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={exchange} onValueChange={(v) => { setExchange(v); setPage(1); }}>
            <SelectTrigger data-testid="filter-exchange" className="h-9 w-[140px] text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200">
              <SelectValue placeholder="Exchange" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
              <SelectItem value="all">All Exchanges</SelectItem>
              {(data?.exchanges || []).map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <DemoBadge />
        </div>

        {isLoading && !data ? (
          <LoadingState label="Loading instruments..." />
        ) : isError ? (
          <ErrorState label="Failed to load instruments" onRetry={() => refetch()} />
        ) : total === 0 ? (
          <EmptyState label="No instruments match your filters" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="instruments-table">
              <thead className="bg-[var(--surface)] sticky top-0">
                <tr className="border-b border-[var(--border-subtle)]">
                  <SortHead col="symbol" label="Symbol" />
                  <SortHead col="name" label="Name" />
                  <th className="px-3 py-2 font-medium text-slate-400">Exchange</th>
                  <th className="px-3 py-2 font-medium text-slate-400">Asset Class</th>
                  <SortHead col="last_price" label="Last Price" align="right" />
                  <SortHead col="change_pct" label="Change %" align="right" />
                  <th className="px-3 py-2 font-medium text-slate-400 text-right">Volume</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((i) => {
                  const up = Number(i.change) >= 0;
                  return (
                    <tr
                      key={i.id}
                      data-testid={`instrument-row-${i.symbol}`}
                      onClick={() => navigate(`/app/market-watch/${i.id}`)}
                      className="border-b border-[var(--border-subtle)] hover:bg-[var(--card-hover)] cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2.5 font-mono-num font-semibold text-slate-100">{i.symbol}</td>
                      <td className="px-3 py-2.5 text-slate-400 max-w-[220px] truncate">{i.name}</td>
                      <td className="px-3 py-2.5"><span className="text-[10px] font-mono text-slate-400 border border-[var(--border-subtle)] rounded px-1.5 py-0.5">{i.exchange}</span></td>
                      <td className="px-3 py-2.5 text-slate-400">{i.asset_class}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-100">{fmtMoney(i.last_price, i.currency)}</td>
                      <td className={cn("px-3 py-2.5 text-right font-mono-num font-semibold", up ? "text-emerald-400" : "text-rose-400")}>
                        {up ? "+" : ""}{i.change_pct}%
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-slate-500">{fmtNum(i.volume, 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between px-3 py-2.5 border-t border-[var(--border-subtle)] text-xs text-slate-500">
          <span data-testid="pagination-info">
            {total === 0 ? "0" : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              data-testid="page-prev"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="w-7 h-7 flex items-center justify-center rounded border border-[var(--border-subtle)] text-slate-400 hover:bg-[var(--card-hover)] disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 font-mono-num text-slate-300">{page} / {totalPages}</span>
            <button
              data-testid="page-next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="w-7 h-7 flex items-center justify-center rounded border border-[var(--border-subtle)] text-slate-400 hover:bg-[var(--card-hover)] disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
